'use client';

import React from 'react';

import {
  ExternalLink,
  Facebook,
  Heart,
  Share2,
  Star,
  Twitter,
} from 'lucide-react';
import Link from 'next/link';

import FaviconImage from '@/components/FaviconImage';

interface ProductHeaderProps {
    name: string;
    link: string;
    openSiteText: string;
    shareUrl: string;
    categoryName?: string;
    categorySlug?: string;
    locale?: string;
    rating?: number;
    ratingCount?: number;
    monthlyVisitors?: string;
    addedDate?: string;
    sponsored?: boolean;
}

const ProductHeader: React.FC<ProductHeaderProps> = ({ 
    name, 
    link, 
    openSiteText, 
    shareUrl, 
    categoryName,
    categorySlug,
    locale = 'en',
    rating,
    ratingCount,
    monthlyVisitors,
    addedDate,
    sponsored = false
}) => {
    const handleShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: name,
                    text: `Check out ${name} - AI Tool`,
                    url: shareUrl,
                });
            } catch (err) {
                console.log('Error sharing:', err);
            }
        }
    };

    return (
        <div className="bg-white shadow-lg rounded-xl overflow-hidden">
            {/* Main Header */}
            <div className="flex flex-col lg:flex-row">
                {/* Left Section - Product Info */}
                <div className="flex-1 p-6 lg:p-8">
                    <div className="flex items-start space-x-4 mb-4">
                        <div className="flex-shrink-0">
                            <FaviconImage
                                website={link}
                                name={name}
                                width={48}
                                height={48}
                                className="rounded-lg shadow-sm"
                            />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-2">
                                <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 truncate">
                                    {name}
                                </h1>
                                <button className="p-2 text-gray-400 hover:text-red-500 transition-colors duration-200">
                                    <Heart className="h-5 w-5" />
                                </button>
                            </div>
                            
                            {/* Category Badge */}
                            {categoryName && categorySlug && (
                                <Link 
                                    href={`/${locale}/category/${categorySlug}`}
                                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors duration-200 mb-3"
                                >
                                    {categoryName}
                                </Link>
                            )}
                            
                            {/* Rating and Stats */}
                            <div className="flex flex-wrap items-center space-x-4 text-sm text-gray-600">
                                {rating && ratingCount && (
                                    <div className="flex items-center space-x-1">
                                        <div className="flex items-center">
                                            {[...Array(5)].map((_, i) => (
                                                <Star 
                                                    key={i} 
                                                    className={`h-4 w-4 ${
                                                        i < Math.floor(rating) 
                                                            ? 'text-yellow-400 fill-current' 
                                                            : 'text-gray-300'
                                                    }`} 
                                                />
                                            ))}
                                        </div>
                                        <span className="font-medium">{rating}</span>
                                        <span>({ratingCount} reviews)</span>
                                    </div>
                                )}
                                
                                {monthlyVisitors && (
                                    <div className="flex items-center space-x-1">
                                        <span className="text-gray-500">👥</span>
                                        <span>{monthlyVisitors} monthly visitors</span>
                                    </div>
                                )}
                                
                                {addedDate && (
                                    <div className="flex items-center space-x-1">
                                        <span className="text-gray-500">📅</span>
                                        <span>Added {addedDate}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Right Section - Actions */}
                <div className="bg-gray-50 px-6 py-4 lg:px-8 lg:py-6 flex flex-col justify-center space-y-3">
                    {/* Primary CTA */}
                    <Link
                        href={link + "?utm_source=aitoolly.com&utm_medium=referral"}
                        target="_blank"
                        rel={sponsored ? 'noopener noreferrer sponsored' : 'noopener noreferrer'}
                        className={`inline-flex items-center justify-center rounded-lg px-6 py-3 font-semibold text-white transition-colors duration-200 shadow-md hover:shadow-lg ${sponsored ? 'bg-[color:var(--editorial-accent)] hover:bg-[color:var(--editorial-accent-dark)]' : 'bg-blue-600 hover:bg-blue-700'}`}
                    >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        {openSiteText}
                    </Link>
                    
                    {/* Secondary Actions */}
                    <div className="flex space-x-2">
                        <button
                            onClick={handleShare}
                            className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors duration-200"
                        >
                            <Share2 className="h-4 w-4 mr-2" />
                            Share
                        </button>
                        
                        <Link
                            href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(`Check out ${name} - AI Tool`)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-blue-50 hover:text-blue-600 transition-colors duration-200"
                            aria-label="Share on Twitter"
                        >
                            <Twitter className="h-4 w-4" />
                        </Link>
                        
                        <Link
                            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-blue-50 hover:text-blue-600 transition-colors duration-200"
                            aria-label="Share on Facebook"
                        >
                            <Facebook className="h-4 w-4" />
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductHeader;
